/**
 * MBA Lab — consent state.
 *
 * Pure and DOM-light on purpose (it touches only `document.cookie` and `localStorage`), so the
 * decision logic can be unit-tested without a browser. `banner.js` owns all the UI.
 *
 * The contract this file defines is duplicated, minimally and deliberately, by the synchronous head
 * script in Views/Shared/_AnalyticsHead.cshtml — that script has to run before gtag.js and cannot
 * wait for the deferred bundle this module ships in. The cookie name and version there must match
 * the constants here; ConsentSmokeTests pins that they do.
 */

/**
 * Bumped when the scope of what we ask for changes. A stored choice at a lower version parses as
 * absent, so the visitor is asked again. Without this, a future change of purpose would silently
 * inherit consent given for something narrower - and it cannot be added retroactively.
 */
export const CONSENT_VERSION = 1;

export const COOKIE_NAME = 'mbalab_consent';
export const STORAGE_KEY = 'mbalab:consent';

/**
 * Six months. CNIL guidance puts the re-prompt interval around here, and one value is used for BOTH
 * outcomes on purpose: storing "accept" for longer than "reject" means people who declined get
 * nagged on every visit while people who accepted never do. That asymmetry is a textbook EDPB
 * "Fickle" deceptive pattern, and it is one of the more commonly enforced ones.
 */
export const MAX_AGE_SECONDS = 15552000;

/** Timezones outside the `Europe/` prefix that are still covered by GDPR or an equivalent regime. */
const STRICT_NON_EUROPE_ZONES = [
  // EU outermost regions - GDPR applies in full, and none of them report as Europe/*.
  'Indian/Reunion',      // Réunion
  'Indian/Mayotte',      // Mayotte
  'America/Martinique',  // Martinique
  'America/Guadeloupe',  // Guadeloupe
  'America/Cayenne',     // French Guiana
  'America/Marigot',     // Saint-Martin
  'Atlantic/Madeira',    // Madeira (PT)
  'Atlantic/Azores',     // Azores (PT)
  'Atlantic/Canary',     // Canary Islands (ES)
  // Quebec: Law 25 requires express, opt-in consent for profiling technologies and requires them
  // off by default, which is functionally the EEA position for analytics.
  'America/Montreal',
  'America/Toronto',     // Ontario/Quebec share this zone in practice; fail closed on both.
];

/**
 * Whether this visitor must opt IN before analytics loads.
 *
 * Deliberately biased to fail closed. We are using basic Consent Mode, which means gtag.js is not
 * loaded at all until consent - and that costs us Google's own IP-based `region` parameter, because
 * that only exists inside the tag we are declining to load. There is no server-side alternative
 * either: response caching means a page rendered for one visitor is served verbatim to the next, so
 * nothing per-visitor can be server-rendered.
 *
 * So this is a timezone heuristic, and it is wrong in exactly one direction on purpose. A US
 * traveller sitting in Paris sees a banner they did not strictly need. Nobody in the EEA gets
 * analytics without asking. An unreadable or missing timezone counts as strict.
 */
export function isStrictRegion() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return true;
    if (zone.indexOf('Europe/') === 0) return true;
    return STRICT_NON_EUROPE_ZONES.indexOf(zone) !== -1;
  } catch (error) {
    return true;
  }
}

/**
 * Global Privacy Control. Twelve US states require honouring this as of 1 January 2026, and it is
 * three lines on a code path we are writing anyway. Applies everywhere, not just those states.
 */
export function gpcEnabled() {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}

/** @returns {string} the serialised cookie/storage value for a decision. */
export function serialize(granted, source) {
  return [
    'v=' + CONSENT_VERSION,
    'analytics=' + (granted ? 'granted' : 'denied'),
    'ts=' + Math.floor(Date.now() / 1000),
    'src=' + (source || 'click'),
  ].join('&');
}

/**
 * @returns {{granted: boolean, ts: number, src: string}|null} null for absent, malformed, expired,
 * or a value written under an older consent version.
 */
export function parse(raw) {
  if (!raw || typeof raw !== 'string') return null;

  const params = {};
  raw.split('&').forEach((pair) => {
    const index = pair.indexOf('=');
    if (index > 0) params[pair.slice(0, index)] = pair.slice(index + 1);
  });

  if (params.v !== String(CONSENT_VERSION)) return null;
  if (params.analytics !== 'granted' && params.analytics !== 'denied') return null;

  const ts = parseInt(params.ts, 10);
  if (!isFinite(ts) || ts <= 0) return null;
  if (Math.floor(Date.now() / 1000) - ts > MAX_AGE_SECONDS) return null;

  return { granted: params.analytics === 'granted', ts, src: params.src || 'unknown' };
}

function readCookie() {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function readStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    return null; // storage disabled or partitioned
  }
}

/** Cookie first, localStorage as a fallback - either can be blocked independently. */
export function read() {
  return parse(readCookie()) || parse(readStorage());
}

/** Dual-writes the decision. */
export function write(granted, source) {
  const value = serialize(granted, source);

  try {
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    // Path=/ is not optional. document.cookie without an explicit path scopes to the *directory* of
    // the current document, so a choice made on /tools/mba-roi-calculator would be invisible on /.
    // SameSite=Lax rather than the Strict used elsewhere in this codebase: Strict is not sent on a
    // cross-site top-level navigation, so a visitor arriving from a search result or an email link
    // would look like they had never chosen.
    document.cookie = COOKIE_NAME + '=' + encodeURIComponent(value)
      + '; Path=/; Max-Age=' + MAX_AGE_SECONDS + '; SameSite=Lax' + secure;
  } catch (error) { /* cookies blocked; storage below may still work */ }

  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch (error) { /* storage blocked; the cookie above may still work */ }

  return value;
}

/**
 * Whether analytics may load right now, with no UI involved.
 *
 * Precedence: an explicit stored choice beats GPC, which beats the regional default. A stored choice
 * wins over GPC because a visitor who came back and clicked Accept made a later and more specific
 * decision than a standing browser preference.
 */
export function analyticsAllowed() {
  const stored = read();
  if (stored) return stored.granted;
  if (gpcEnabled()) return false;
  return !isStrictRegion();
}

/** Whether to show the first-run bar: no stored choice yet, and GPC has not already decided. */
export function needsPrompt() {
  return !read() && !gpcEnabled();
}

/**
 * Whether GA has actually stored an identifier on this device.
 *
 * Used to position the preferences switch when there is no stored choice. Rendering it ON would be
 * a pre-ticked consent box (unlawful under Planet49); rendering it OFF would misrepresent a US
 * visitor whose analytics is genuinely running. The presence of _ga is the observable truth, needs
 * no geo lookup, and is readable because GA does not set it HttpOnly.
 */
export function hasAnalyticsCookie() {
  if (typeof document === 'undefined' || !document.cookie) return false;
  return /(?:^|;\s*)_ga(_[A-Z0-9]+)?=/.test(document.cookie);
}

/**
 * Expires GA's cookies on withdrawal.
 *
 * A consent update to denied stops gtag reading and writing, but leaves any identifier already on
 * the device there indefinitely - so "withdraw" without this is not withdrawal. GA writes _ga on the
 * registrable domain, so both that and the host must be cleared.
 */
export function clearAnalyticsCookies() {
  if (typeof document === 'undefined') return [];

  const names = (document.cookie || '')
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => /^_ga(_[A-Z0-9]+)?$/.test(name) || name === '_gid');

  const host = typeof location !== 'undefined' ? location.hostname : '';
  const parts = host.split('.');
  const registrable = parts.length > 2 ? '.' + parts.slice(-2).join('.') : '.' + host;
  const domains = ['', '; Domain=' + host, '; Domain=' + registrable];

  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = name + '=; Path=/; Max-Age=0' + domain;
    });
  });

  return names;
}
